import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet';
import {
  Stethoscope,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ArrowRight,
  ChevronRight,
  Clock,
  Scale,
  Building2,
  GraduationCap,
  FileText,
  Users,
  Calendar,
  Target,
  Briefcase,
} from 'lucide-react';

const ActGuidePage = () => {
  const keyPoints = [
    {
      icon: Scale,
      title: 'Legal Priority for UK Graduates',
      description: 'UK medical graduates now have statutory priority for Foundation Year and Specialty Training posts, significantly reducing available training positions for IMGs.',
    },
    {
      icon: Clock,
      title: 'Secondary Priority for Experienced IMGs',
      description: 'IMGs with substantial NHS experience may qualify for secondary priority. This makes gaining NHS experience through LED/Trust Grade roles essential.',
    },
    {
      icon: Building2,
      title: 'LED & Trust Grade Roles Unaffected',
      description: 'Locally Employed Doctor and Trust Grade positions are NOT affected by the Act. These remain fully open to IMGs and are the fastest route to NHS experience.',
    },
    {
      icon: Calendar,
      title: '2026 Implementation',
      description: 'The Act takes effect from 2026. IMGs planning to apply for specialty training in 2027 and beyond need to start building NHS experience immediately.',
    },
  ];

  const whatYouCanDo = [
    {
      step: '1',
      title: 'Secure an LED or Trust Grade Position',
      description: 'These non-training NHS posts are your fastest route to gaining the clinical experience you need. They\'re not affected by the priority system.',
    },
    {
      step: '2',
      title: 'Build Your NHS Experience',
      description: 'Time spent in LED, Trust Grade, Staff Grade, and Clinical Fellow roles counts towards your NHS experience for secondary priority consideration.',
    },
    {
      step: '3',
      title: 'Document Everything',
      description: 'Keep detailed records of your NHS experience, including dates, roles, responsibilities, and any achievements or additional training.',
    },
    {
      step: '4',
      title: 'Set Up Job Alerts',
      description: 'LED and Trust Grade positions fill quickly. Use TRACJobsAlert to get instant notifications when new positions matching your criteria are posted.',
    },
  ];

  const faqs = [
    {
      question: 'Does this Act apply to all doctor jobs in the UK?',
      answer: 'No. The Act specifically affects Foundation Programme and Specialty Training posts. Locally Employed Doctor (LED), Trust Grade, Staff Grade, and Clinical Fellow positions are NOT affected and remain fully open to IMGs.',
      links: [
        { to: '/img-guide/led-jobs', text: 'Learn about LED jobs →' },
        { to: '/img-guide/trust-grade-jobs', text: 'Learn about Trust Grade jobs →' },
      ],
    },
    {
      question: 'I\'m already in an NHS training post. Am I affected?',
      answer: 'If you\'re currently in a training programme, you should be able to continue. The Act primarily affects new applications for training posts from 2026 onwards.',
    },
    {
      question: 'How much NHS experience do I need for secondary priority?',
      answer: 'The exact requirements are being finalised, but substantial NHS experience (typically measured in years) in clinical roles will be considered. Start building experience now to maximise your eligibility.',
    },
    {
      question: 'Can I still work as a doctor in the NHS as an IMG?',
      answer: 'Absolutely. The Act does not prevent IMGs from working in the NHS. LED, Trust Grade, Staff Grade, Specialty Doctor, and Clinical Fellow positions remain fully available to qualified IMGs.',
    },
    {
      question: 'What types of jobs should I apply for as an IMG?',
      answer: 'Focus on Trust Grade, LED (Locally Employed Doctor), Staff Grade, and Clinical Fellow positions. These provide valuable NHS experience and are not subject to the training priority system.',
      links: [
        { to: '/img-guide/trust-grade-jobs', text: 'Trust Grade Guide →' },
        { to: '/img-guide/led-jobs', text: 'LED Jobs Guide →' },
        { to: '/img-guide/plab-jobs', text: 'PLAB Doctor Guide →' },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Medical Training Prioritisation Act 2026: What IMGs Need to Know | TRACJobsAlert</title>
        <meta name="description" content="Complete guide to the Medical Training Prioritisation Act 2026 for International Medical Graduates. Learn how it affects your career, what NHS experience you need, and how to secure LED & Trust Grade positions." />
        <meta name="keywords" content="Medical Training Prioritisation Act IMG impact, IMG NHS experience prioritisation 2026, how to qualify for NHS training prioritisation IMG, LED job NHS IMG experience, Trust Grade job NHS IMG, IMG not prioritised specialty training" />
        <link rel="canonical" href="https://tracjobsalert.co.uk/img-guide/medical-training-prioritisation-act-2026" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-lg text-slate-900">TRACJobsAlert</span>
                  <span className="text-xs text-emerald-600">IMG Guide</span>
                </div>
              </Link>
              <div className="flex items-center gap-3">
                <Link to="/img-guide">
                  <Button variant="ghost" className="text-slate-600 hover:text-emerald-600">
                    ← Back to IMG Guide
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                    Get Job Alerts
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Alert Banner */}
        <div className="bg-red-500 text-white py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">This legislation significantly impacts IMG career pathways in the UK.</p>
              <p className="text-red-100 text-sm mt-1">
                If you're an IMG planning to pursue specialty training, you need to understand these changes and act now.
              </p>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
              <Link to="/img-guide" className="hover:text-emerald-600">IMG Guide</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-900">Medical Training Prioritisation Act 2026</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              Medical Training Prioritisation Act 2026:{' '}
              <span className="text-red-600">What IMGs Need to Know</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-6">
              The Medical Training (Prioritisation) Act 2026 introduces legal priority for UK graduates in Foundation 
              and Specialty Training applications. Here's what it means for international medical graduates and 
              what you can do to protect your career pathway.
            </p>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Last updated: February 2025
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                10 min read
              </span>
            </div>
          </div>
        </section>

        {/* Key Points */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
              Key Points You Must Understand
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {keyPoints.map((point, index) => (
                <div key={index} className="bg-white p-6 rounded-xl border border-slate-200">
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center mb-4">
                    <point.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{point.title}</h3>
                  <p className="text-slate-600 text-sm">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What Jobs Are Affected */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
              Which Jobs Are Affected?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                <div className="flex items-center gap-2 mb-4">
                  <XCircle className="w-6 h-6 text-red-600" />
                  <h3 className="font-semibold text-red-900">Affected by Priority System</h3>
                </div>
                <ul className="space-y-3 text-red-800">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0"></span>
                    <span>Foundation Programme (FY1/FY2)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0"></span>
                    <span>Core Training (CT1/CT2)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0"></span>
                    <span>Specialty Training (ST1-ST8)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0"></span>
                    <span>GP Training</span>
                  </li>
                </ul>
              </div>
              <div className="bg-emerald-50 p-6 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  <h3 className="font-semibold text-emerald-900">NOT Affected - Open to IMGs</h3>
                </div>
                <ul className="space-y-3 text-emerald-800">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></span>
                    <span><strong>Locally Employed Doctor (LED)</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></span>
                    <span><strong>Trust Grade Doctor</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></span>
                    <span><strong>Staff Grade Doctor</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></span>
                    <span><strong>Clinical Fellow</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></span>
                    <span><strong>Specialty Doctor (SAS)</strong></span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* What You Can Do */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 text-center">
              What You Should Do Now
            </h2>
            <p className="text-slate-600 text-center mb-8 max-w-2xl mx-auto">
              Time is critical. Here's your action plan to build the NHS experience you need.
            </p>
            <div className="space-y-6">
              {whatYouCanDo.map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-xl border border-slate-200 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-slate-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-slate-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.question}</h3>
                  <p className="text-slate-600">{faq.answer}</p>
                  {faq.links && (
                    <div className="flex flex-wrap gap-4 mt-3">
                      {faq.links.map((link, linkIndex) => (
                        <Link key={linkIndex} to={link.to} className="text-emerald-600 text-sm font-medium hover:text-emerald-700">
                          {link.text}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 text-center">
              Learn More About IMG-Friendly NHS Jobs
            </h2>
            <p className="text-slate-600 text-center mb-8 max-w-2xl mx-auto">
              Explore our detailed guides on the types of positions that remain fully open to international medical graduates.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/img-guide/trust-grade-jobs" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors group">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-colors">
                  <Briefcase className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Trust Grade Doctor Jobs</h3>
                <p className="text-slate-600 text-sm mb-4">Complete guide to Trust Grade positions — salaries, requirements, and how to apply.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
              <Link to="/img-guide/led-jobs" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors group">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">LED Doctor Jobs</h3>
                <p className="text-slate-600 text-sm mb-4">Everything about Locally Employed Doctor roles — LED vs Trust Grade explained.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
              <Link to="/img-guide/plab-jobs" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors group">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                  <GraduationCap className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">PLAB Doctor Jobs</h3>
                <p className="text-slate-600 text-sm mb-4">Your path after passing PLAB — what jobs to apply for and how to succeed.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-emerald-500">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Start Building Your NHS Experience Today
            </h2>
            <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
              LED and Trust Grade positions are your fastest route to NHS experience. Get instant alerts when new positions matching your criteria are posted.
            </p>
            <Link to="/register">
              <Button className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg font-semibold">
                Get Job Alerts Now - £9.99/month
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-slate-400 py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold text-white">TRACJobsAlert</span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <Link to="/img-guide" className="hover:text-white transition-colors">IMG Guide</Link>
                <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              </div>
              <p className="text-sm">© 2025 TRACJobsAlert. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ActGuidePage;
